import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  PageHeader,
  SelectField,
  StatusBadge,
} from "@nahal/ui";
import { useIncidentInbox } from "../../lib/queries";

/**
 * Cross-customer by design: support does not start the day knowing which
 * customer is failing. A product that reports nothing contributes no rows at
 * all, so an empty inbox reads the same whether nothing is broken or nothing is
 * wired up yet -- which is why the empty state says both.
 */
export function IncidentInboxScreen() {
  const inbox = useIncidentInbox();
  const navigate = useNavigate();
  const [severity, setSeverity] = useState("");

  if (inbox.isLoading) return <LoadingBlock />;

  const groups = (inbox.data ?? [])
    .map((group) => ({
      ...group,
      incidents: severity
        ? group.incidents.filter(
            (incident) => (incident.severity ?? "").toLowerCase() === severity,
          )
        : group.incidents,
    }))
    .filter((group) => group.incidents.length > 0);

  const total = groups.reduce((sum, group) => sum + group.incidents.length, 0);

  return (
    <>
      <PageHeader
        title="Incident inbox"
        description="Everything failing, across every customer and product."
        meta={<StatusBadge status={`${total} open`} tone={total > 0 ? "warning" : "success"} />}
      />

      <Card className="mb-4">
        <div className="p-4">
          <SelectField
            label="Severity"
            options={[
              { value: "", label: "All severities" },
              { value: "critical", label: "Critical" },
              { value: "error", label: "Error" },
              { value: "warning", label: "Warning" },
              { value: "info", label: "Info" },
            ]}
            value={severity}
            onChange={(event) => setSeverity(event.target.value)}
          />
        </div>
      </Card>

      {groups.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck className="size-5" />}
          title="Nothing failing"
          description="Either every product is healthy, or none of them report incidents yet."
        />
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <Card key={group.workspaceId}>
              <CardHeader
                title={`${group.organizationName} - ${group.workspaceName} (${group.productCode})`}
                action={
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigate({
                        to: "/customers/$organizationId",
                        params: { organizationId: group.organizationId },
                      })
                    }
                  >
                    Open customer
                  </Button>
                }
              />
              <ul className="divide-y divide-[var(--border-subtle)]">
                {group.incidents.map((incident, index) => (
                  <li key={`${incident.code}-${index}`} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {incident.code ?? "unknown"}
                      </span>
                      <StatusBadge status={incident.severity ?? "info"} />
                    </div>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">{incident.message}</p>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-2xs text-[var(--text-muted)]">
                      <span className="numeric">seen {incident.count}x</span>
                      {incident.source && <span>{incident.source}</span>}
                      {incident.lastSeen && (
                        <span>last {new Date(incident.lastSeen).toLocaleString()}</span>
                      )}
                      {incident.requestId && <code>{incident.requestId}</code>}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
